import React, { useEffect, useRef, useState } from 'react'
import {
	Field, getFormValues, InjectedFormProps, reduxForm
} from 'redux-form'
import {
	Checkbox, Col, Form, Radio, Row, Tooltip
} from 'antd'
import cx from 'classnames'
import { useTranslation } from 'react-i18next'
import { map } from 'lodash'
import { useSelector } from 'react-redux'
import {
	ArrowRightOutlined, EnvironmentOutlined, InboxOutlined, LockOutlined, PlusOutlined, SwapOutlined, UnlockOutlined
} from '@ant-design/icons'
import { FORMS, STORE_ITEM_TRANSACTION_TYPE, translateStoreItemTransactionTypeToSk } from '../../../utils/enums'
import TextField from '../../../atoms/form/TextField'
import validateCreateStoreItemTransactionForm from './validateCreateStoreItemTransactionForm'
// eslint-disable-next-line import/no-cycle
import JobSelectField from './JobSelectField'
// eslint-disable-next-line import/no-cycle
import { ICreateStoreItemTransactionForm } from '../../../reducers/storeItemTransactions/actions'
import RadioSelectField from '../../../atoms/form/RadioSelectField'
// eslint-disable-next-line import/no-cycle
import { STORE_TYPE } from '../../stores/Stores'
// eslint-disable-next-line import/no-cycle
import { RootState } from '../../../reducers'
// eslint-disable-next-line import/no-cycle
import { ContextStore, LockedSide } from '../types'
// eslint-disable-next-line import/no-cycle
import { JobStoreItem } from '../../../reducers/jobs/actions'

type ComponentProps = {
	isCreate: boolean,
	onTransactionTypeChanged: (type: STORE_ITEM_TRANSACTION_TYPE) => void,
	contextStore?: ContextStore | null,
	lockedSide: LockedSide,
	// "Pripísať" z detailu zákazky - pripisuje sa cez virtuálny sklad rovno na zákazku
	isViaStoreMode: boolean,
	onSwap: () => void,
	onUnlockRequest: () => void,
	onRelock: (side: Exclude<LockedSide, null>) => void
}

type Props = InjectedFormProps<ICreateStoreItemTransactionForm, ComponentProps> & ComponentProps

const CreateStoreItemTransactionForm = (props: Props) => {
	const { t } = useTranslation('components')
	const {
		handleSubmit,
		contextStore,
		lockedSide,
		isViaStoreMode,
		onSwap,
		onUnlockRequest,
		onRelock,
		change
	} = props
	const hasPermissionToAddStoreItems = useSelector((state: RootState) => state.profile.profile.data?.hasPermissionToAddStoreItems)
	const storeItemForm: ICreateStoreItemTransactionForm = useSelector((state) => getFormValues(FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM)(state)) as ICreateStoreItemTransactionForm
	const storeTransactionType = storeItemForm?.type

	useEffect(() => { props.onTransactionTypeChanged(storeTransactionType) }, [storeTransactionType])

	// adresa zvoleného skladu pre pásik priebehu a texty - stav sa zrkadlí do formulára kvôli validácii
	const [viaStore, setViaStore] = useState<JobStoreItem | null>(null)

	const handleViaStoreSelected = (option: JobStoreItem | null) => {
		setViaStore(option)
		change('viaStoreStock', option?.storeItemsCount ?? null)
	}

	/**
	 * Sklad, cez ktorý sa pripisuje, predvyplníme - vo väčšine organizácií je virtuálny sklad
	 * jediný a klikať doň zakaždým je zbytočná práca.
	 * Predvyplní sa len raz pri vstupe do módu, aby sme používateľovi nevnucovali sklad späť,
	 * keď si ho vedome vymaže alebo keď si v poli niečo vyhľadáva.
	 */
	const didPrefillViaStore = useRef(false)

	useEffect(() => {
		if (!isViaStoreMode) {
			didPrefillViaStore.current = false
		}
	}, [isViaStoreMode])

	const handleViaStoreOptionsLoaded = (options: JobStoreItem[]) => {
		if (!isViaStoreMode || didPrefillViaStore.current || storeItemForm?.viaJobID || !options.length) {
			return
		}
		didPrefillViaStore.current = true
		// pri viacerých skladoch ponúkni ten s najväčšou zásobou - najskôr postačí na samotný presun
		const best = [...options].sort((a, b) => (b.storeItemsCount || 0) - (a.storeItemsCount || 0))[0]
		change('viaJobID', best.id)
		handleViaStoreSelected(best)
	}

	const viaStoreStock = storeItemForm?.viaStoreStock || 0
	const amountValue = Number(storeItemForm?.amount) || 0
	const useExistingStock = !!storeItemForm?.useExistingStock
	const hasEnoughStock = amountValue > 0 && viaStoreStock >= amountValue
	const isMissingStock = useExistingStock && !!storeItemForm?.viaJobID && !hasEnoughStock

	const transactionOptions = hasPermissionToAddStoreItems
		? [STORE_ITEM_TRANSACTION_TYPE.IN, STORE_ITEM_TRANSACTION_TYPE.REGULAR, STORE_ITEM_TRANSACTION_TYPE.CONSUME]
		: [STORE_ITEM_TRANSACTION_TYPE.REGULAR, STORE_ITEM_TRANSACTION_TYPE.CONSUME]

	/**
	 * Ikona zámku sa zobrazuje vždy len pri jednej adrese - pri tej, kde práve sedí kontextový sklad
	 * (sklad / zákazka, z ktorého detailu bol dialóg otvorený).
	 */
	const renderLock = (side: Exclude<LockedSide, null>) => {
		if (!contextStore) {
			return null
		}

		// pri pripísaní cez sklad je cieľ neoddeliteľnou súčasťou operácie - zámok je len indikátor
		if (isViaStoreMode && side === 'TO') {
			return (
				<Tooltip title={ t('components:Adresa je pevne daná zákazkou, z ktorej ste dialóg otvorili.') }>
					<span className={ 'transaction-lock-btn static' }>
						<LockOutlined style={ { fontSize: 12 } }/>
					</span>
				</Tooltip>
			)
		}

		if (lockedSide === side) {
			return (
				<Tooltip title={ t('components:Adresa je pevne daná skladom, z ktorého ste dialóg otvorili. Kliknutím odomknete.') }>
					<button
						type={ 'button' }
						className={ 'transaction-lock-btn' }
						aria-label={ t('components:Odomknúť transakciu') }
						onClick={ onUnlockRequest }
					>
						<LockOutlined style={ { fontSize: 12 } }/>
					</button>
				</Tooltip>
			)
		}

		// odomknutý stav - ponúkni zamknutie späť, ale len pri strane, kde kontextový sklad reálne je
		const valueOnSide = side === 'FROM' ? storeItemForm?.jobFromID : storeItemForm?.jobToID
		if (lockedSide === null && valueOnSide === contextStore.id) {
			return (
				<Tooltip title={ `${t('components:Znovu zamknúť na')} ${contextStore.label}` }>
					<button
						type={ 'button' }
						className={ 'transaction-lock-btn unlocked' }
						aria-label={ t('components:Znovu zamknúť') }
						onClick={ () => onRelock(side) }
					>
						<UnlockOutlined style={ { fontSize: 12 } }/>
					</button>
				</Tooltip>
			)
		}

		return null
	}

	return (
		<Form onSubmitCapture={ handleSubmit }>
			<Row gutter={ 16 }>
				<Col span={ 24 } className={ 'grid' }>
					<div className={ 'flex direction-col justify-start main-content' }>
						<Field
							name={ 'type' }
							component={ RadioSelectField }
							label={ t('components:Typ transakcie') }
							defaultValueKey={ STORE_ITEM_TRANSACTION_TYPE.REGULAR }
							required={true}
							options={ map(transactionOptions, (transaction) => (
								<Radio.Button
									key={ transaction }
									value={ transaction }
								>
									{ translateStoreItemTransactionTypeToSk(transaction) }
								</Radio.Button>
							))
							}
						/>
						<Field
							name={ 'amount' }
							component={ TextField }
							type={'number'}
							label={ t('components:Množstvo') }
							size={ 'large' }
							required
						/>
						{(storeTransactionType === STORE_ITEM_TRANSACTION_TYPE.REGULAR || storeTransactionType === STORE_ITEM_TRANSACTION_TYPE.CONSUME) && (
							<Field
								name={ 'jobFromID' }
								component={ JobSelectField }
								storeType={null}
								storeItemID={storeItemForm?.storeItemID}
								label={'Adresa z'}
								size={ 'large' }
								filterStoreWithZeroStock={true}
								locked={ lockedSide === 'FROM' }
								contextOption={ contextStore }
								labelSuffix={ renderLock('FROM') }
								allowClear
								showSearch
							/>
						)}
						{ (storeTransactionType === STORE_ITEM_TRANSACTION_TYPE.REGULAR && <div className={'transaction-swap-row'}>
							<Tooltip title={ t('components:Prehodí smer prevodu - z čoho sa odoberá a kam sa dodáva') }>
								<button
									type={ 'button' }
									className={ 'transaction-swap-btn' }
									aria-label={ t('components:Vymeniť Adresu z a Adresu do') }
									onClick={ onSwap }
								>
									<SwapOutlined style={ { fontSize: 18 } }/>
									<span className={ 'transaction-swap-label' }>{ t('components:Vymeniť Adresu z a Adresu do') }</span>
								</button>
							</Tooltip>
						</div>) }
						{ isViaStoreMode && (
							<>
								<div className={ 'transaction-flow' }>
									<div className={ cx('transaction-flow-node', { muted: useExistingStock }) }>
										<div className={ 'transaction-flow-icon' }><PlusOutlined/></div>
										<div className={ 'transaction-flow-cap' }>{ t('components:Pripísať') }</div>
										<div className={ 'transaction-flow-name' }>{ t('components:nový tovar') }</div>
									</div>
									<div className={ cx('transaction-flow-arrow', { muted: useExistingStock }) }><ArrowRightOutlined/></div>
									<div className={ 'transaction-flow-node' }>
										<div className={ 'transaction-flow-icon' }><InboxOutlined/></div>
										<div className={ 'transaction-flow-cap' }>{ t('components:Cez sklad') }</div>
										<div className={ 'transaction-flow-name' } title={ viaStore?.address }>{ viaStore?.address || '—' }</div>
									</div>
									<div className={ 'transaction-flow-arrow' }><ArrowRightOutlined/></div>
									<div className={ 'transaction-flow-node target' }>
										<div className={ 'transaction-flow-icon' }><EnvironmentOutlined/></div>
										<div className={ 'transaction-flow-cap' }>{ t('components:Adresa do') }</div>
										<div className={ 'transaction-flow-name' } title={ contextStore?.label }>{ contextStore?.label }</div>
									</div>
								</div>
								<Field
									name={ 'viaJobID' }
									component={ JobSelectField }
									storeType={ STORE_TYPE.VIRTUAL_STORE }
									storeItemID={ storeItemForm?.storeItemID }
									label={ t('components:Pripísať cez') }
									size={ 'large' }
									// pripisovať sa musí dať aj do skladu, ktorý položku nikdy nemal
									filterStoreWithZeroStock={ false }
									onOptionSelected={ handleViaStoreSelected }
									onOptionsLoaded={ handleViaStoreOptionsLoaded }
									// nedostatok zásob je problém len pri presune - pri pripisovaní nového tovaru nie
									labelSuffix={ storeItemForm?.viaJobID
										? <span className={ cx('stock-pill', { low: isMissingStock, neutral: !isMissingStock && !hasEnoughStock }) }>
											{ `Na sklade: ${viaStoreStock} ks` }
										</span>
										: null }
									required
									allowClear
									showSearch
								/>
								{ /* label obaľuje checkbox - klik kdekoľvek na karte ho prepne (bez htmlFor, to by klik zdvojilo) */ }
								<label className={ cx('transaction-check-card', { on: useExistingStock }) }>
									<Checkbox
										id={ 'useExistingStock' }
										checked={ useExistingStock }
										onChange={ (e) => change('useExistingStock', e.target.checked) }
									/>
									<span className={ 'transaction-check-body' }>
										<span className={ 'transaction-check-title' }>{ t('components:Presunúť z aktuálnych skladových zásob') }</span>
										<span className={ 'transaction-check-desc' }>
											{ useExistingStock && viaStore
												? `Nepripíše sa nový tovar — ${amountValue} ks sa iba presunie zo skladu ${viaStore.address}.`
												: 'Nepripisovať nový tovar — použiť to, čo už na sklade je.' }
										</span>
									</span>
								</label>
							</>
						) }
						{(storeTransactionType === STORE_ITEM_TRANSACTION_TYPE.REGULAR || storeTransactionType === STORE_ITEM_TRANSACTION_TYPE.IN) && (
							<Field
								name={'jobToID'}
								component={JobSelectField}
								storeItemID={null}
								storeType={storeTransactionType === STORE_ITEM_TRANSACTION_TYPE.IN ? STORE_TYPE.VIRTUAL_STORE : null}
								label={'Adresa do'}
								size={'large'}
								locked={ lockedSide === 'TO' }
								contextOption={ contextStore }
								labelSuffix={ renderLock('TO') }
								allowClear
								showSearch
							/>
						)}
						{ isViaStoreMode && !!storeItemForm?.viaJobID && (
							<div className={ cx('transaction-summary', { error: isMissingStock }) }>
								<span className={ 'transaction-summary-icon' }>{ isMissingStock ? '!' : 'i' }</span>
								<span>
									{ isMissingStock && (
										`Na sklade ${viaStore?.address || ''} je len ${viaStoreStock} ks, potrebujete ${amountValue} ks. Odškrtnite checkbox alebo znížte množstvo.`
									) }
									{ !isMissingStock && useExistingStock && (
										`Vytvorí sa 1 transakcia: prevod ${amountValue} ks zo skladu ${viaStore?.address || ''} na ${contextStore?.label}.`
									) }
									{ !isMissingStock && !useExistingStock && (
										`Vytvoria sa 2 transakcie: pripísanie ${amountValue} ks do skladu ${viaStore?.address || ''} a hneď prevod na ${contextStore?.label}.`
									) }
									{ !useExistingStock && hasEnoughStock && (
										<span className={ 'transaction-summary-tip' }>
											{ `Tip: sklad má dosť zásob (${viaStoreStock} ks) — môžete len presunúť.` }
										</span>
									) }
								</span>
							</div>
						) }
					</div>
				</Col>
			</Row>
		</Form>
	)
}

const form = reduxForm<ICreateStoreItemTransactionForm, ComponentProps>({
	form: FORMS.CREATE_STORE_ITEM_TRANSACTION_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateCreateStoreItemTransactionForm
})(CreateStoreItemTransactionForm)

export default form
